import { Framework } from 'vuetify';

export type ThemePreference = 'light' | 'dark' | 'system';

// Plain localStorage (not secure-ls) so the pre-paint script in
// public/index.html can read it before any bundle loads.
const STORAGE_KEY = 'ka.theme';

const media = (): MediaQueryList => window.matchMedia('(prefers-color-scheme: dark)');

export function getPreference(): ThemePreference {
  const value = localStorage.getItem(STORAGE_KEY);
  return value === 'light' || value === 'dark' ? value : 'system';
}

export function resolveIsDark(preference: ThemePreference = getPreference()): boolean {
  return preference === 'dark' || (preference === 'system' && media().matches);
}

export function applyTheme(vuetify: Framework, preference: ThemePreference = getPreference()): void {
  const dark = resolveIsDark(preference);
  vuetify.theme.dark = dark;
  document.documentElement.setAttribute('data-ka-theme', dark ? 'dark' : 'light');
}

export function setPreference(vuetify: Framework, preference: ThemePreference): void {
  localStorage.setItem(STORAGE_KEY, preference);
  applyTheme(vuetify, preference);
}

export function toggleTheme(vuetify: Framework): void {
  setPreference(vuetify, vuetify.theme.dark ? 'light' : 'dark');
}

// Follow OS theme changes live while preference is 'system'. Call once at boot.
export function watchSystemTheme(vuetify: Framework): void {
  const mq = media();
  const handler = () => {
    if (getPreference() === 'system') {
      applyTheme(vuetify);
    }
  };
  if (mq.addEventListener) {
    mq.addEventListener('change', handler);
  } else {
    mq.addListener(handler); // older Safari
  }
}
